// Angular
import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// Material
import { MatDialog } from '@angular/material';
// RxJS
import { Observable, BehaviorSubject, Subscription, of, combineLatest, Subject, merge } from 'rxjs';
import { map, startWith, delay, first, distinctUntilChanged, filter, tap, debounceTime } from 'rxjs/operators';
// NGRX
import { Store, select } from '@ngrx/store';
import { Dictionary, Update } from '@ngrx/entity';
import { AppState } from '../../../../../core/reducers';
// Layout
import { SubheaderService, LayoutConfigService } from '../../../../../core/_base/layout';
// CRUD
import { LayoutUtilsService, TypesUtilsService, MessageType } from '../../../../../core/_base/crud';
// Services and Models
import { 
	Shift,
	selectShiftById,
	ShiftsDataSource,
	ShiftGetAll,
  ShiftAdd,
  selectLastCreatedShiftId,
  ShiftEdit,
  selectLastUpdatedShiftId,
  ShiftType,
  selectShiftTypes,
  ShiftGetTypes
} from '../../../../../core/plant-configuration';
import { User, UsersDataSource } from '../../../../../core/auth';
import { selectAuthState } from '../../../../../core/auth/_selectors/auth.selectors';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'kt-shifts-edit',
  templateUrl: './shifts-edit.component.html',
  styleUrls: ['./shifts-edit.component.scss']
})
export class ShiftsEditComponent implements OnInit, OnDestroy {
	@ViewChild('wizard', {static: true}) el: ElementRef;
	// Public properties
	shift: Shift;
	shiftId$: Observable<number>;
	oldShift: Shift;
	selectedTab = 0;
	loadingSubject = new BehaviorSubject<boolean>(true);
	loading$: Observable<boolean>;
	shiftForm: FormGroup;
	hasFormErrors = false;
	shiftsDataSource: ShiftsDataSource;
	shiftsResult: Shift[] = [];
  	shiftTypesResult: ShiftType[];
  	selectedShiftType: ShiftType = new ShiftType();
	private subscriptions: Subscription[] = [];
    private user:User = new User();
	private weekday = [
		"LUN",
		"MAR",
		"MIE",
		"JUE",
		"VIE",
		"SAB",
		"DOM"
	];
	
	startTime = {hour: 8, minute: 30};
	endTime = {hour: 16, minute: 30};
	collationTime = {hour: 1, minute: 0};

	// Private password
	private componentSubscriptions: Subscription;
	// sticky portlet header margin
	private headerMargin: number;

	/**
	 * Component constructor
	 *
	 * @param store: Store<AppState>
	 * @param activatedRoute: ActivatedRoute
	 * @param router: Router
	 * @param typesUtilsService: TypesUtilsService
	 * @param formBuilder: FormBuilder
	 * @param dialog: MatDialog
	 * @param subheaderService: SubheaderService
	 * @param layoutUtilsService: SubheaderService
	 * @param layoutConfigService: LayoutConfigService
	 * @param cdr: ChangeDetectorRef
	 */
	constructor(
		private store: Store<AppState>,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private typesUtilsService: TypesUtilsService,
		private formBuilder: FormBuilder,
		public dialog: MatDialog,
		private subheaderService: SubheaderService,
		private layoutUtilsService: LayoutUtilsService,
		private layoutConfigService: LayoutConfigService,
		private cdr: ChangeDetectorRef) {
	}

	/**
	 * @ Lifecycle sequences => https:/iiot_code
iiot_code/angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit() {

		this.user.clear();

		const authSubscription = this.store.pipe(
			select(selectAuthState),
		).subscribe((auth) => {
			// console.log("auth state: "+JSON.stringify(auth));
			if(auth.isUserLoaded)
				this.user = auth.user;
			else
				console.log("shift edit component: usuario no logueado");
		});

		this.loading$ = this.loadingSubject.asObservable();
		this.loadingSubject.next(true);
		
		const shiftTypes$ = this.store.pipe(
			select(selectShiftTypes)
		);

		const shiftTypesSub = shiftTypes$.subscribe(
			(shiftTypes)=>{
				this.shiftTypesResult = shiftTypes;
				console.log("shift types: "+JSON.stringify(shiftTypes));
				if(shiftTypes && this.shiftForm && shiftTypes.length>0){
					this.shiftForm.get("type").patchValue(shiftTypes[0].name);
					this.selectShiftType(shiftTypes[0]);
					this.cdr.detectChanges();
				}
			}
		)

    	// Init DataSource
		this.shiftsDataSource = new ShiftsDataSource(this.store);

		const routeSub = this.activatedRoute.params.subscribe(params => {
			const id = params.id;
			if (id && id > 0) {
				const shift$ = this.store.pipe(
					select(selectShiftById(id))
				);
				const sub = shift$.subscribe(
					(shift)=>{
						if(shift){
							this.loadShift(shift);
						}							
					}
				)
				this.subscriptions.push(sub);
			} else {
                var shift:Shift = new Shift();
                shift.clear();
                this.loadShift(shift);
			}
		});
        this.loadShifts();
        
		// sticky portlet header
		window.onload = () => {
			const style = getComputedStyle(document.getElementById('kt_header'));
			this.headerMargin = parseInt(style.height, 0);
		};
		const wizard = new KTWizard(this.el.nativeElement, {
			startStep: 1
		});

		// Validation before going to next page
		wizard.on('beforeNext', (wizardObj) => {
			// https://angular.io/guide/forms
			// https://angular.io/guide/form-validation

			// validate the form and use below function to stop the wizard's step
			// wizardObj.stop();
		});

		// Change event
		wizard.on('change', () => {
			setTimeout(() => {
				KTUtil.scrollTop();
			}, 500);
		});
		this.subscriptions.push(authSubscription);
		this.subscriptions.push(shiftTypesSub);
		this.subscriptions.push(routeSub);
		this.loadShiftTypes();
	}
	loadShifts(){
		this.store.dispatch(new ShiftGetAll({}));
	}

	loadShift(shift: Shift, fromService: boolean = false) {
		if (!shift) {
			this.goBack('');
		}
		this.shift = Object.assign(new Shift(), shift);
		this.shiftId$ = of(shift.id);
		this.oldShift = Object.assign({}, shift);

		this.initShift();
		
		if (fromService) {
			this.cdr.detectChanges();
		}
	  }
	  
	loadShiftTypes(){
		this.store.dispatch(new ShiftGetTypes({}));
	}

	/**
	 * On destroy
	 */
	ngOnDestroy() {
		if (this.componentSubscriptions) {
			this.componentSubscriptions.unsubscribe();			
		}
		this.subscriptions.forEach((sub)=>sub.unsubscribe());
	}

	/**
	 * Init shift
	 */
	initShift() {
		// console.log("create form");
		this.createForm();
		this.loadingSubject.next(false);
		// if (!this.shift.id) {
		// 	this.subheaderService.setBreadcrumbs([
		// 		{ title: 'Listado de turnos', page: `/plant-configuration/shifts` },
		// 		{ title: 'Crear turno', page: `/plant-configuration/shifts/add` }
		// 	]);
		// 	return;
		// }
		// this.subheaderService.setTitle('Crear turno');
		// this.subheaderService.setBreadcrumbs([
		// 	{ title: 'Listado de turno', page: `/plant-configuration/shifts` },
		// 	{ title: 'Editar turno', page: `/plant-configuration/shifts/edit`, queryParams: { id: this.shift.id } }
		// ]);
	}

	/**
	 * Create form
	 */
	createForm() {
		// console.log("creating shift form");
		// console.log(JSON.stringify(this.shift));
		this.shiftForm = this.formBuilder.group({
			name: [this.shift.name, Validators.required],
			type: [this.shift.shift_type_name, Validators.required],
			startDay: [this.shift.start_time_day, Validators.required],
			startTime: [this.startTime, Validators.required],
			endTime: [this.endTime, Validators.required],
			collationTime: [this.collationTime, Validators.required],
			endDay: [this.shift.end_time_day, Validators.required],
			valid_from: [this.shift.valid_from, Validators.required],
			valid_to: [this.shift.valid_to, Validators.required],
		});
		this.shift.collation_time_minutes = this.collationTime.hour*60 + this.collationTime.minute;
		this.shift.start_time_hour = this.startTime.hour;
		this.shift.start_time_minute = this.startTime.minute;
		this.shift.end_time_hour = this.endTime.hour;
		this.shift.end_time_minute = this.endTime.minute;

		const controls = this.shiftForm.controls;
		const nameSub = controls.name.valueChanges.subscribe(x=>{
			this.shift.name = x;
		});
		const startDaySub = controls.startDay.valueChanges.subscribe(x=>{
			this.shift.start_time_day = x;
			this.calcShiftTime(this.shift);
		});
		const startTimeSub = controls.startTime.valueChanges.subscribe(x=>{
			this.shift.start_time_hour = x.hour;
			this.shift.start_time_minute = x.minute;
			this.startTime = x;
			this.calcShiftTime(this.shift);
		});
		const collationTimeSub = controls.collationTime.valueChanges.subscribe(x=>{
			this.shift.collation_time_minutes = x.hour*60 + x.minute;
			this.collationTime = x;
			this.calcShiftTime(this.shift);
		});
		const endTimeSub = controls.endTime.valueChanges.subscribe(x=>{
			this.shift.end_time_hour = x.hour;
			this.shift.end_time_minute = x.minute;
			this.endTime = x;
			this.calcShiftTime(this.shift);
		});
		const endDaySub = controls.endDay.valueChanges.subscribe(x=>{
			this.shift.end_time_day = x;
			this.calcShiftTime(this.shift);
		});
		const validFromSub = controls.valid_from.valueChanges.subscribe(x=>{
			this.shift.valid_from = x;
		})
		const validToSub = controls.valid_to.valueChanges.subscribe(x=>{
			this.shift.valid_to = x;
		})
		this.subscriptions.push(nameSub);
		this.subscriptions.push(startDaySub);
		this.subscriptions.push(endDaySub);
		this.subscriptions.push(startTimeSub);
		this.subscriptions.push(endTimeSub);
		this.subscriptions.push(collationTimeSub);
		this.subscriptions.push(validFromSub);
		this.subscriptions.push(validToSub);
		// console.log("shift form created");
		// console.log(this.shiftForm);
		// console.log(typeof(this.shiftForm));
		// console.log(JSON.stringify(this.shiftForm));

		// this.filteredBirdTypes = this.shiftForm.controls.birdType.valueChanges
		// 	.pipe(
		// 		startWith(''),
		// 		map(val => this.filterBirdType(val.toString())),
		// 		tap(birdTypes => {
		// 			this.selectedBirdType = null;
		// 			if(birdTypes && birdTypes.length>0){
		// 				let val = this.shiftForm.controls.birdType.value;
		// 				let birdTypeCoincidences = birdTypes.filter((birdType)=>birdType.name.toLowerCase()==val.toLowerCase());
		// 				if(birdTypeCoincidences.length>0){
		// 					this.selectedBirdType = birdTypeCoincidences[0];
		// 				}
		// 			}
		// 		})
		// 	);
		// this.filteredShiftTypes = this.shiftForm.controls.shiftType.valueChanges
		// 	.pipe(
		// 		startWith(''),
		// 		map(val => this.filterShiftType(val.toString())),
		// 		tap(shiftTypes => {
		// 			this.selectedShiftType = null;
		// 			if(shiftTypes && shiftTypes.length>0){
		// 				let val = this.shiftForm.controls.shiftType.value;
		// 				let shiftTypeCoincidences = shiftTypes.filter((shiftType)=>shiftType.name.toLowerCase()==val.toLowerCase());
		// 				if(shiftTypeCoincidences.length>0){
		// 					this.selectedShiftType = shiftTypeCoincidences[0];
		// 				}
		// 			}
		// 		})
		// 	);
		this.cdr.detectChanges();
	}

	/**
	 * Go back to the list
	 *
	 * @param id: any
	 */
	goBack(id) {
		this.loadingSubject.next(false);
		const url = `/plant-configuration/shifts?id=${id}`;
		this.router.navigateByUrl(url, { relativeTo: this.activatedRoute });
	}

	goBackWithoutId() {
		this.router.navigateByUrl('/plant-configuration/shifts', { relativeTo: this.activatedRoute });
	}

	/**
	 * Refresh shift
	 *
	 * @param isNew: boolean
	 * @param id: number
	 */
	refreshShift(isNew: boolean = false, id = 0) {
		this.loadingSubject.next(false);
		let url = this.router.url;
		if (!isNew) {
			this.router.navigate([url], { relativeTo: this.activatedRoute });
			return;
		}

		url = `/plant-configuration/shifts/edit/${id}`;
		this.router.navigateByUrl(url, { relativeTo: this.activatedRoute });
	}

	/**
	 * Reset
	 */
	reset() {
		this.shift = Object.assign({}, this.oldShift);
		this.createForm();
		this.hasFormErrors = false;
		this.shiftForm.markAsPristine();
		this.shiftForm.markAsUntouched();
		this.shiftForm.updateValueAndValidity();
	}

	findShiftType(name:String):ShiftType{
		return this.shiftTypesResult.filter(shiftType=>shiftType.name.toLowerCase() == name.toLowerCase())[0];
	}

	/**
	 * Save data
	 *
	 * @param withBack: boolean
	 */
	onSumbit(withBack: boolean = false) {
		this.hasFormErrors = false;
		const controls = this.shiftForm.controls;
		/** check form */
		if (this.shiftForm.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);

			this.hasFormErrors = true;
			this.selectedTab = 0;
			return;
		}

		// tslint:disable-next-line:prefer-const
		let shift = this.prepareData();

		if (shift.id > 0) {
			this.updateShift(shift, withBack);
			return;
		}

		this.addShift(shift, true);
	}

	/**
	 * Returns object for saving
	 */
	prepareData(): Shift {
		const controls 			= this.shiftForm.controls;
		let shift: Shift = new Shift();
		shift.clear();
		shift.id = this.shift.id;
		shift.name = controls.name.value;
		shift.start_time_day = controls.startDay.value;
		shift.end_time_day = controls.endDay.value;
		shift.start_time_hour = this.startTime.hour;
		shift.end_time_hour = this.endTime.hour;
		shift.start_time_minute = this.startTime.minute;
		shift.end_time_minute = this.endTime.minute;
		shift.collation_time_minutes = this.collationTime.hour*60 + this.collationTime.minute;
		shift.shift_type_id = this.selectedShiftType.id;
		shift.shift_type_name = this.selectedShiftType.name;
		shift.valid_from = controls.valid_from.value;
		shift.valid_to = controls.valid_to.value;
		this.calcShiftTime(shift);
		delete shift.company_id ;
		shift.write_uid = this.user.id;
		console.log("prepared shift: "+ JSON.stringify(shift));
		return shift;
	}

	/*
		Constituye el tiempo en minutos del turno 
		NOCHE
		end_time_hour: 				6
		end_time_minute:   			20
		collation_time_minutes:		45
		start_time_hour:			20
		start_time_minute:			40+
	*/
	// calcShiftTime(shift:Shift){
	// 	if (shift.shift_type_id){
	// 		shift.shift_duration_minutes = (24 - shift.start_time_hour)*60 + shift.start_time_minute + shift.end_time_hour*60 + shift.end_time_minute - shift.collation_time_minutes;
	// 	}else{
	// 		shift.shift_duration_minutes = (shift.end_time_hour-shift.start_time_hour)*60 + (shift.end_time_minute-shift.start_time_minute) - shift.collation_time_minutes;	
	// 	}		
	// }
	calcShiftTime(shift:Shift){
		if(shift.end_time_hour*60+shift.end_time_minute > shift.start_time_hour*60 + shift.start_time_minute){
			shift.shift_duration_minutes = (shift.end_time_hour-shift.start_time_hour)*60 + (shift.end_time_minute-shift.start_time_minute) - shift.collation_time_minutes;
		}else{
			shift.shift_duration_minutes = (24 - shift.start_time_hour)*60 - shift.start_time_minute + shift.end_time_hour*60 + shift.end_time_minute - shift.collation_time_minutes;
		}
	}

	/**
	 * Add Shift
	 *
	 * @param shift: Shift
	 * @param withBack: boolean
	 */
	addShift(shift:Shift, withBack:boolean = false) {
		this.loadingSubject.next(true);
		
		this.store.dispatch(new ShiftAdd({shift: shift}));
		const addBreedingSub = this.store.pipe(
			delay(1000),
			select(selectLastCreatedShiftId)
		).subscribe(newId => {
			if (!newId || newId==-1) {
				// addBreedingSub.unsubscribe();
				return;
			}

			this.loadingSubject.next(false);
			if (withBack) {
				this.goBack(newId);
			} else {
				const message = `Turno ha sido creado exitosamente.`;
				this.layoutUtilsService.showActionNotification(message, MessageType.Create, 10000, true, true);
				this.refreshShift(true, newId);
			}
			// addBreedingSub.unsubscribe();
		});
		this.subscriptions.push(addBreedingSub);
	}

	/**
	 * Update shift
	 *
	 * @param shift: Shift
	 * @param withBack: boolean
	 */
	updateShift(shift: Shift, withBack: boolean = false) {
		this.loadingSubject.next(true);

		const updateShift: Update<Shift> = {
			id: shift.id,
			changes: shift
		};

		this.store.dispatch(new ShiftEdit({
			partialShift: updateShift,
			shift:shift
		}));

		const updateBreedingSub = this.store.pipe(
			delay(1000),
			select(selectLastUpdatedShiftId)
		).subscribe((id) => { // Remove this line
			if (!id || id==-1) {
				// updateBreedingSub.unsubscribe();
				return;
			}
			if (withBack) {
				this.goBack(shift.id);
			} else {
				const message = `Turno ha sido actualizado exitosamente.`;
				this.layoutUtilsService.showActionNotification(message, MessageType.Update, 10000, true, true);
				// this.refreshShift(false);
				this.goBackWithoutId();
			}
			// updateBreedingSub.unsubscribe();
		}); // Remove this line
		this.subscriptions.push(updateBreedingSub);
	}

	/**
	 * Returns component title
	 */
	getComponentTitle() {
		let result = 'Crear turno';
		if (!this.shift || !this.shift.id) {
			return result;
		}

		result = `Editar turno - ${this.shift.name}`;
		return result;
	}

	/**
	 * Close alert
	 *
	 * @param $event
	 */
	onAlertClose($event) {
		this.hasFormErrors = false;
	}

	selectShiftType(shiftType:ShiftType){
		this.selectedShiftType = shiftType;
	}
}